/**
 * Ghost Performance Report Page
 * Displays ghost performance metrics and analytics
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableHead,
  Chip,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner } from '../../components/common';
import { GhostPerformanceService } from '../../services/reports/GhostPerformanceService';
import { formatDate } from '../../utils/dateUtils';
import type { GhostPerformanceData, KPICard, ReportFilters } from '../../types/reports';
import {
  Card,
  CardContent,
  TableCell,
  TableHeaderCell,
  TableRow,
  Select,
  TextField,
  DataTablePagination,
} from '../../components/ui';

const GhostPerformancePage: React.FC = () => {
  const theme = useTheme();
  const [data, setData] = useState<GhostPerformanceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(4);
  const [filters, setFilters] = useState<ReportFilters>({
    dateRange: 'Last 30 Days',
    region: 'All Regions',
  });
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await GhostPerformanceService.getGhostPerformanceData(
        filters,
        page + 1,
        rowsPerPage
      );
      setData(result);
    } catch (error) {
      console.error('Failed to fetch ghost performance data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getTrendIcon = (trend?: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon sx={{ fontSize: 14 }} />;
      case 'down':
        return <TrendingDownIcon sx={{ fontSize: 14 }} />;
      default:
        return <RemoveIcon sx={{ fontSize: 14 }} />;
    }
  };

  const getTrendColor = (trend?: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return theme.palette.success.main;
      case 'down':
        return theme.palette.error.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return theme.palette.success.main;
      case 'Idle':
        return theme.palette.warning.main;
      case 'Offline':
        return theme.palette.error.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const renderKPICard = (kpi: KPICard) => (
    <Card
      sx={{
        border: `1px solid ${theme.customColors.tableBorder}`,
        height: '100%',
      }}
    >
      <CardContent>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            marginBottom: theme.spacing(1),
            fontWeight: 500,
          }}
        >
          {kpi.title}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
          }}
        >
          {kpi.value}
        </Typography>
        {kpi.change && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
            <Box sx={{ color: getTrendColor(kpi.trend) }}>
              {getTrendIcon(kpi.trend)}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: getTrendColor(kpi.trend),
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              {kpi.change}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const filteredGhostList = data?.ghostList.filter((ghost) =>
    ghost.ghostName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ghost.ghostId.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const paginatedGhostList = filteredGhostList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (isLoading && !data) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No data available.
        </Typography>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Page Header */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(0.5),
          }}
        >
          Ghost Performance Report
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(3),
          }}
        >
          Monitor and analyze the performance of investigators.
        </Typography>

        {/* Filters */}
        <Box
          sx={{
            display: 'flex',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(3),
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Select
            size="small"
            label="Date Range"
            value={filters.dateRange || 'Last 30 Days'}
            onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as string })}
            formControlProps={{ sx: { minWidth: 180 } }}
          >
            <MenuItem value="Last 24 Hours">Last 24 Hours</MenuItem>
            <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
            <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
            <MenuItem value="Last 90 Days">Last 90 Days</MenuItem>
            <MenuItem value="Custom">Custom</MenuItem>
          </Select>

          <Select
            size="small"
            label="Region"
            value={filters.region || 'All Regions'}
            onChange={(e) => setFilters({ ...filters, region: e.target.value as string })}
            formControlProps={{ sx: { minWidth: 150 } }}
          >
            <MenuItem value="All Regions">All Regions</MenuItem>
            <MenuItem value="Michigan">Michigan</MenuItem>
            <MenuItem value="National">National</MenuItem>
          </Select>

          <TextField
            size="small"
            placeholder="Search and select a Ghost..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: 300,
            }}
          />

          <IconButton
            sx={{
              color: theme.palette.text.secondary,
              border: `1px solid ${theme.customColors.tableBorder}`,
            }}
          >
            <FilterIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Performance Overview KPI Cards */}
      <Grid container spacing={3} sx={{ marginBottom: theme.spacing(4) }}>
        <Grid item xs={12} sm={6} md={3}>
          {renderKPICard(data.performanceOverview.avgTasksCompleted)}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKPICard(data.performanceOverview.acceptanceRate)}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKPICard(data.performanceOverview.cancellationRate)}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKPICard(data.performanceOverview.avgResponseTime)}
        </Grid>
      </Grid>

      {/* Task Performance Table */}
      <Card
        sx={{
          border: `1px solid ${theme.customColors.tableBorder}`,
          marginBottom: theme.spacing(4),
        }}
      >
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              marginBottom: theme.spacing(2),
              fontSize: '1rem',
            }}
          >
            Task Performance
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>GHOST NAME</TableHeaderCell>
                <TableHeaderCell>TASKS COMPLETED</TableHeaderCell>
                <TableHeaderCell>ACCEPTANCE RATE</TableHeaderCell>
                <TableHeaderCell>AVG. RATING</TableHeaderCell>
                <TableHeaderCell>STATUS</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedGhostList.map((ghost) => (
                <TableRow key={ghost.ghostId}>
                  <TableCell>
                    {ghost.ghostName}
                  </TableCell>
                  <TableCell>
                    {ghost.tasksCompleted}
                  </TableCell>
                  <TableCell>
                    {ghost.acceptanceRate.toFixed(0)}%
                  </TableCell>
                  <TableCell>
                    {ghost.avgRating.toFixed(1)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ghost.status}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(ghost.status),
                        color: theme.palette.common.white,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <DataTablePagination
            totalPages={Math.ceil(filteredGhostList.length / rowsPerPage)}
            page={page + 1}
            onPageChange={(_, newPage) => setPage(newPage - 1)}
            showStats
            startIndex={page * rowsPerPage + 1}
            endIndex={Math.min((page + 1) * rowsPerPage, filteredGhostList.length)}
            totalResults={filteredGhostList.length}
            layout="space-between"
          />
        </CardContent>
      </Card>

      {/* Charts Section */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(2),
                  fontSize: '1rem',
                }}
              >
                Quality & Compliance
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.qualityAndCompliance}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ status, percent }: any) => `${status}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    innerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {data.qualityAndCompliance.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.customColors.tableBorder}`,
                      borderRadius: theme.shape.borderRadius,
                    }}
                    labelStyle={{ color: theme.palette.text.primary }}
                    formatter={(value: number) => `${value}%`}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(0.5),
                  fontSize: '1rem',
                }}
              >
                Earnings & Engagement
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  marginBottom: theme.spacing(2),
                  fontSize: '0.75rem',
                }}
              >
                Online vs. Active Time (Last 7 Days)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.earningsAndEngagement}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                  <XAxis
                    dataKey="date"
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                    tickFormatter={(value) => formatDate(value)}
                  />
                  <YAxis
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.customColors.tableBorder}`,
                      borderRadius: theme.shape.borderRadius,
                    }}
                    labelStyle={{ color: theme.palette.text.primary }}
                    formatter={(value: number) => `${value}h`}
                  />
                  <Legend />
                  <Bar dataKey="onlineTime" fill={theme.palette.primary.main} name="Online Time" />
                  <Bar dataKey="activeTime" fill={theme.palette.secondary.main} name="Active Time" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </MainLayout>
  );
};

export default GhostPerformancePage;

